if (!customElements.get('gn-ingredients-carousel')) {
  customElements.define('gn-ingredients-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-ingredient-track]');
      this.cards = [...this.querySelectorAll('[data-ingredient-card]')];
      this.controls = this.querySelector('[data-ingredient-controls]');
      this.dots = [...this.querySelectorAll('[data-ingredient-dot]')];
      this.previous = this.querySelector('[data-ingredient-previous]');
      this.next = this.querySelector('[data-ingredient-next]');
      this.status = this.querySelector('[data-ingredient-status]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.index = 0;
      this.pendingIndex = null;
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index), options));
      this.addEventListener('keydown', event => {
        if (!this.mobile.matches || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('a, input, textarea, select')) return;
        const current = this.pendingIndex ?? this.index;
        const direction = this.rtl ? -1 : 1;
        const destinations = { ArrowLeft: current - direction, ArrowRight: current + direction, Home: 0, End: this.cards.length - 1 };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        this.go(destinations[event.key]);
      }, options);
      this.track.addEventListener('pointerdown', () => { this.pendingIndex = null; }, options);
      this.track.addEventListener('scroll', () => {
        if (!this.mobile.matches) return;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.pendingIndex = null;
          this.sync();
          this.announce();
        }, 180);
      }, { ...options, passive: true });
      this.mobile.addEventListener('change', () => this.layout(), options);
      this.observer = new ResizeObserver(() => this.layout());
      this.observer.observe(this.track);
      this.layout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settleTimer);
    }

    layout() {
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      this.controls.hidden = !this.mobile.matches;
      this.track.tabIndex = this.mobile.matches ? 0 : -1;
      if (!this.mobile.matches) {
        this.track.scrollTo({ left: 0, behavior: 'instant' });
        this.index = 0;
        this.pendingIndex = null;
        this.status.textContent = '';
      } else this.sync();
      this.update();
    }

    position(index) {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const offset = Math.abs(this.cards[index].offsetLeft - this.cards[0].offsetLeft);
      return Math.min(offset, max);
    }

    sync() {
      const current = Math.abs(this.track.scrollLeft);
      let nearest = 0;
      this.cards.forEach((card, index) => {
        if (Math.abs(this.position(index) - current) < Math.abs(this.position(nearest) - current)) nearest = index;
      });
      this.index = nearest;
      this.update();
    }

    update() {
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.cards.length - 1;
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    announce() {
      this.status.textContent = this.dataset.statusTemplate.replace('[index]', String(this.index + 1));
    }

    move(direction) { this.go((this.pendingIndex ?? this.index) + direction); }

    go(index) {
      if (!this.mobile.matches) return;
      this.index = Math.max(0, Math.min(index, this.cards.length - 1));
      this.pendingIndex = this.index;
      this.update();
      this.announce();
      this.track.scrollTo({ left: this.position(this.index) * (this.rtl ? -1 : 1), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }
  });
}
